import { debounce } from 'lodash';
import { createDebug } from 'obug';
import { permissionApi } from '@/services';
import { checkPermission } from '@pages/modules/permission/utils';
import { READ_PAGE } from '@pages/modules/permission/constants';
import { runtimeInfo } from '@utils/fetch';
import { shallAIPageKeepAlive } from './utils/keep-alive';
const debug = createDebug('wuji-gogo:xy-client:vue3router');
let vue3Router;
let vue2Router;
let baseInfo = {
    base: '/',
    env: 'dev',
    projectId: '',
    branch: '',
};
export function setVue3RouterBaseInfo(info) {
    baseInfo = info;
}
/**
 * 两个 router 对 query 的编码规则不同：vue-router 3 用 encodeURIComponent（`:` `/` `?` `=`
 * 全部编码），vue-router 4 用 encodeURI（这几个字符原样保留）。同一个地址在两边序列化出的
 * fullPath 并不相等，直接比字符串会让守卫永远认为「没对齐」。
 * 所以跨 router 比较一律先过一遍 vue2 的 resolve，统一到 vue2 的编码再比。
 */
const toVue2FullPath = (fullPath) => vue2Router.resolve(fullPath).route.fullPath;
/**
 * forcePath 内嵌场景：判断 vue3 Memory Router 是否已与外层 vue2 当前 URL 对齐。
 * 无 vue2 router 时视为已对齐，避免孤儿/非 runtime 场景死等。
 */
export function isVue3SyncedWithVue2(vue3FullPath) {
    if (!vue2Router)
        return true;
    const vue2FullPath = vue2Router.currentRoute?.fullPath;
    return !!vue2FullPath && toVue2FullPath(vue3FullPath) === vue2FullPath;
}
export function createRouterForGogo(router4Module, Vue3) {
    if (vue3Router)
        return vue3Router;
    const { createRouter, createMemoryHistory, START_LOCATION } = router4Module;
    const { h, watchEffect, effectScope } = Vue3;
    debug('[vue3router][gogo] createRouterForGogo', baseInfo);
    vue3Router = createRouter({
        history: createMemoryHistory(baseInfo.base),
        routes: [
            // TODO 虽然 gogo 会动态注册，但是这可能也要一些404之类的默认页面
            { path: '/', component: { render: () => h('div', ['404']) } },
        ],
    });
    // 做绑定的事情
    vue2Router = window.xy_runtime_router; // ./src/router/runtimeModeRouter.js
    if (vue2Router) {
        if (lastVue3SyncPayload)
            notifyVue3RouterChanged(lastVue3SyncPayload);
        const scope = effectScope(true);
        scope.run(() => {
            // Memory Router 首次安装和 runtime 重新挂载都会从 START_LOCATION 发起导航。
            // 必须先对齐外层实际地址，再执行权限校验；否则初始化的 / 会被当成访问首页。
            // 此处不能受路由表同步状态限制，首次同步完成前也可能安装 router。
            vue3Router.beforeEach((to, from) => {
                if (from === START_LOCATION) {
                    const vue2Path = vue2Router.currentRoute.fullPath;
                    return toVue2FullPath(to.fullPath) !== vue2Path ? vue2Path : undefined;
                }
            });
            // 必须排在下面的同步守卫之前：同步守卫会把 vue2 推向目标页，
            // 排在它后面时拦截已经晚了，vue2 那边已经开始导航
            vue3Router.beforeEach(async (to) => {
                // 预览模式下 pageId 可能还没保存生成，无从鉴权，直接放过
                // eslint-disable-next-line no-underscore-dangle
                if (window.__gogoDebugHijack)
                    return;
                // 取不到 pageId 的是通配符路由和 403 自身，放过，否则 403 会再次被拦形成死循环
                const { pageId } = vue2Router.resolve(to.fullPath).route.meta;
                if (!pageId)
                    return;
                const { projectId, env } = baseInfo;
                const userView = window.xy_runtime_userview
                    ?? await permissionApi.fetchUserView(projectId, env);
                if (checkPermission(userView, READ_PAGE, pageId))
                    return;
                vue2Router.push({
                    path: runtimeInfo().page403Path,
                    query: { permission: READ_PAGE, pageId, url: String(location.href) },
                });
                return false;
            });
            const debouncedVue2Push = debounce(vue2Router.push.bind(vue2Router), 0, { trailing: true });
            vue3Router.beforeEach((to) => {
                if (isSyncing || !lastVue3SyncPayload)
                    return;
                if (vue2Router.currentRoute.fullPath !== toVue2FullPath(to.fullPath)) {
                    debouncedVue2Push.cancel();
                    debouncedVue2Push(to.fullPath);
                }
            });
            const debouncedVue3Push = debounce(vue3Router.push.bind(vue3Router), 0, { trailing: true });
            vue2Router.beforeEach((to, from, next) => {
                if (!isSyncing && lastVue3SyncPayload) {
                    if (toVue2FullPath(vue3Router.currentRoute.value.fullPath) !== to.fullPath) {
                        debouncedVue3Push.cancel();
                        debouncedVue3Push(to.fullPath);
                    }
                }
                next();
            });
            watchEffect(() => {
                debug('[vue-router][gogo] getRoutes', [...vue3Router.getRoutes()]);
            });
        });
    }
    else {
        debug('[vue-router][gogo] cannot locate runtime vue-router instance');
    }
    return vue3Router;
}
let isSyncing = false;
let lastVue3SyncPayload = null;
export async function notifyVue3RouterChanged(payload) {
    if (!vue2Router || !vue3Router) {
        // not ready? next time sync
        lastVue3SyncPayload = payload;
        return;
    }
    // 好像没有删除路由的能力，先不管了
    // for (const r of lastVue3SyncPayload?.pages || []) {
    //   vue2Router.addRoute
    // }
    try {
        isSyncing = true;
        const GogoRuntimeRouterView = payload.useRemoteRuntime
            ? require('./GogoPortalRuntimeRouterView.vue').default
            : require('./GogoRuntimeRouterView.vue').default;
        for (const r of payload.pages) {
            const pageId = `[gogo-page]${r.file}`;
            const keepAlive = shallAIPageKeepAlive(r.routePath, r.meta);
            vue2Router.addRoute({
                path: r.routePath,
                name: pageId,
                component: GogoRuntimeRouterView,
                props: {
                    projectId: baseInfo.projectId,
                    pageId,
                },
                meta: {
                    // TODO: 各种 pageAttr 的东西？
                    pageId,
                    pageName: pageId,
                    pageAttr: {
                        navFixTop: 'system',
                        customNav: 'system',
                        customSidebar: 'system',
                        customHeader: 'system',
                        customFooter: 'system',
                        showWatermark: 'system',
                        keepAlive,
                        isMobile: false,
                    },
                    isKeepAlive: keepAlive,
                    layoutType: 'header-sidebar-content',
                    pageType: 'content',
                },
            });
        }
        await vue3Router.replace(vue2Router.currentRoute.fullPath);
    }
    catch (e) {
        console.error('[vue-router][gogo] sync error', e);
    }
    isSyncing = false;
    lastVue3SyncPayload = JSON.parse(JSON.stringify(payload)); // lodash的原型可能有问题
}
/**
 * React 链路的路由注册：只注册 Vue2 路由，不操作 Vue3 Router。
 * React 内部通过 MemoryRouter 自行管理路由。
 */
let lastReactSyncPayload = null;
export function notifyReactRouterChanged(payload) {
    const router = window.xy_runtime_router;
    if (!router) {
        lastReactSyncPayload = payload;
        console.warn('[vue-router][gogo-react] vue2 router not ready, deferring');
        return;
    }
    try {
        const GogoPortalRuntimeRouterView = require('./GogoPortalRuntimeRouterView.vue').default;
        for (const r of payload.pages) {
            const pageId = `[gogo-page]${r.file}`;
            const keepAlive = shallAIPageKeepAlive(r.routePath, r.meta);
            router.addRoute({
                path: r.routePath,
                name: pageId,
                component: GogoPortalRuntimeRouterView,
                props: {
                    projectId: baseInfo.projectId,
                    pageId,
                },
                meta: {
                    pageId,
                    pageName: pageId,
                    pageAttr: {
                        navFixTop: 'system',
                        customNav: 'system',
                        customSidebar: 'system',
                        customHeader: 'system',
                        customFooter: 'system',
                        showWatermark: 'system',
                        keepAlive,
                        isMobile: false,
                    },
                    isKeepAlive: keepAlive,
                    layoutType: 'header-sidebar-content',
                    pageType: 'content',
                },
            });
        }
        lastReactSyncPayload = JSON.parse(JSON.stringify(payload));
    }
    catch (e) {
        console.error('[vue-router][gogo-react] sync error', e);
    }
}
